/**
 * URL-persisted single-value filter — the plain-string sibling of
 * `useTimeRangeParam`, and it keeps the same disciplines for the same reasons.
 *
 *   ?status=at_risk&tier=platinum&q=okafor
 *
 * THE URL IS THE SOURCE OF TRUTH. The value is derived from `useSearchParams()`
 * on every render; there is no shadow `useState` that could drift from the
 * address bar. That is the whole point: a filtered grid becomes a link an
 * operator can paste into a message, and a refresh lands on the same rows.
 *
 * A STALE BOOKMARK IS FORGIVEN, NEVER FATAL. When `allowed` is given and the
 * URL holds something else — a status that was renamed, a tier that was
 * retired, a typo — the hook falls back to `defaultValue` and renders. A filter
 * screen that throws on a bad query string is a filter screen that punishes the
 * person for having saved a link.
 *
 * CLEAN URLS. Writing the default (or an empty string) DELETES the key rather
 * than parking `?status=` on the address bar, so the unfiltered page has a bare
 * URL and two people who navigated there differently share one link.
 *
 * REPLACE, NOT PUSH. Filters are not navigation. Pushing would fill the back
 * button with every keystroke of a debounced search box, and the way out of a
 * screen would be twenty presses deep.
 *
 * ONLY THIS KEY IS TOUCHED. Writes clone the existing params, so the
 * unprefixed `range`/`from`/`to` keys the same pages already carry — and any
 * other hook's key — survive untouched.
 *
 * `defaultValue` and `allowed` are read as of the FIRST render: they are screen
 * constants, and capturing them keeps the returned setter stable against an
 * inline array literal churning on every render.
 */

import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseUrlParamOptions {
  /** Returned when the key is absent, empty, or holds a value outside `allowed`. */
  defaultValue?: string;
  /** The values this screen accepts. Omit for free text (a search box). */
  allowed?: readonly string[];
}

export function useUrlParam(
  key: string,
  opts: UseUrlParamOptions = {}
): [string, (next: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Screen constants, captured once — see the header note.
  const defaultRef = useRef<string>(opts.defaultValue ?? '');
  const allowedRef = useRef<readonly string[] | undefined>(opts.allowed);

  const raw = searchParams.get(key);

  let value = defaultRef.current;
  if (raw !== null && raw !== '') {
    const allowed = allowedRef.current;
    if (!allowed || allowed.includes(raw)) value = raw;
  }

  const setValue = useCallback(
    (next: string) => {
      const allowed = allowedRef.current;
      // Never write a value this hook would refuse to read back: an allowlisted
      // param that could hold an unreadable value is a URL that lies about the
      // grid underneath it.
      const normalised = allowed && !allowed.includes(next) ? defaultRef.current : next;

      const params = new URLSearchParams(searchParams);
      if (normalised === defaultRef.current || normalised === '') params.delete(key);
      else params.set(key, normalised);

      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, key]
  );

  return [value, setValue];
}

export default useUrlParam;
